/**
 * Centralized configuration for Data Disclaimer content
 * This can be easily updated without touching component code
 * Future enhancement: Load from backend API for dynamic content management
 */

export interface DisclaimerSection {
  title: string;
  content: string[];
}

export interface DisclaimerConfig {
  version: string;
  lastUpdated: string;
  sections: DisclaimerSection[];
  dataDelays: {
    gsc: string;
    ga4: string;
    bing: string;
  };
}

export const disclaimerConfig: DisclaimerConfig = {
  version: '1.0',
  lastUpdated: 'January 16, 2026',
  dataDelays: {
    gsc: '2-3 days delay',
    ga4: '1-2 days delay',
    bing: '2-4 days delay',
  },
  sections: [
    {
      title: '1. Data Accuracy & Reliability',
      content: [
        'All data is sourced from third-party platforms (Google, Bing, GA4)',
        'We do not guarantee 100% accuracy due to API limitations and data processing delays',
        'Metrics are statistical approximations and may contain sampling bias',
        'Historical data may be revised by source platforms without notice',
      ],
    },
    {
      title: '2. Risk Acceptance',
      content: [
        'You acknowledge that SEO decisions based on this data carry inherent risks',
        'Data delays may cause missed opportunities or delayed issue detection',
        'Anomaly detection uses statistical thresholds and may produce false positives/negatives',
        'OCI scores are predictive models, not guarantees of SEO performance',
      ],
    },
    {
      title: '3. No Warranty & Liability',
      content: [
        'This platform is provided "AS IS" without warranties of any kind',
        'We are not responsible for business losses due to data inaccuracies or delays',
        'Users must verify critical data points before making major business decisions',
        'Always cross-reference with source platforms for validation',
      ],
    },
    {
      title: '4. Data Privacy & Security',
      content: [
        'Your data is stored securely and not shared with third parties',
        'OAuth credentials are encrypted and refreshed automatically',
        'You can revoke access at any time through your Google/Bing account settings',
        'We comply with GDPR and data protection regulations',
      ],
    },
    {
      title: '5. Usage Guidelines',
      content: [
        'Use this platform as a decision-support tool, not the sole decision maker',
        'Combine insights with your domain expertise and business context',
        'Monitor data health dashboard for source connection issues',
        'Report anomalies or unexpected patterns to support team',
      ],
    },
  ],
};

/**
 * Future: Fetch disclaimer config from backend API
 * This allows admins to update content without deploying frontend changes
 */
export const fetchDisclaimerConfig = async (): Promise<DisclaimerConfig> => {
  // TODO: Implement API call to backend
  // const response = await fetch('/api/v1/config/disclaimer');
  // return response.json();
  
  // For now, return static config
  return disclaimerConfig;
};
